/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import { getFilterElementsDefault, Input, useWorkplaceState } from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    addRejectedTrafficItem,
    deleteRejectedTrafficItem,
    getRejectedTraffic,
    getRejectedTrafficExcel,
    getRejectedTrafficItem,
    updateRejectedTrafficItem,
} from 'axios/rejectedTrafficApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    RejectedTraffic,
    RejectedTrafficAdd,
    RejectedTrafficChange,
    RejectedTrafficFilter as RejectedTrafficFilterType,
    RejectedTrafficInfo,
} from 'models/api/rejectedTraffic';
import { useCallback, useState } from 'react';
import { FormatterProps } from 'react-data-grid';
import { Trans } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { deleteActionBtnFormatter } from 'utils/columnFormatters';
import './RejectedTraffic.scss';
import RejectedTrafficAddButton from './RejectedTrafficAddButton';
import RejectedTrafficModal from './RejectedTrafficModal';

type RejectedTrafficWPFormatterProps = FormatterProps<RejectedTraffic, RejectedTraffic[]>;

const RejectedTrafficWP = () => {
    const api = useApi();
    const queryClient = useQueryClient();
    const { hasRole } = useAuth();
    const [isModalVisible, setIsModalVisible] = useState(false);

    const logoVirtualPathFormatter = useCallback(
        ({ row }: RejectedTrafficWPFormatterProps) => (
            <img src={row.logoVirtualPath} alt={''} className="p-2" />
        ),
        []
    );

    const isActiveFormatter = useCallback(
        ({ row }: RejectedTrafficWPFormatterProps) => (
            <Input type="checkbox" name="notUsed" checked={row.isActive ?? false} disabled />
        ),
        []
    );

    return (
        <BOWorkplace<
            RejectedTraffic,
            RejectedTrafficInfo,
            RejectedTrafficFilterType,
            RejectedTrafficChange,
            RejectedTrafficAdd
        >
            testId="rejected-traffic"
            filterParams={{}}
            getFilterElements={getFilterElementsDefault([])}
            deleteMutationFn={(id) => deleteRejectedTrafficItem(api, id)}
            lineHeight={70}
            fetchExcelQueryFn={(filter) => getRejectedTrafficExcel(api, filter)}
            modal={{
                component: isModalVisible && (
                    <RejectedTrafficModal isOpen={isModalVisible} toggle={setIsModalVisible} />
                ),
                fetchCurrentItemQueryFn: (id) => getRejectedTrafficItem(api, id),
                changeMutationFn: (item) => updateRejectedTrafficItem(api, item),
                addMutationFn: (item) => addRejectedTrafficItem(api, item),
            }}
            showModal={
                hasRole('rejectedTrafficGetById') ? () => setIsModalVisible(true) : undefined
            }
            buttons={
                hasRole('rejectedTrafficCreate')
                    ? [
                          <RejectedTrafficAddButton
                              key="1"
                              showModal={() => setIsModalVisible(true)}
                          />,
                      ]
                    : []
            }
            showExcelBtn={hasRole('rejectedTrafficExcel')}
            fetchQueryFn={(filter) => getRejectedTraffic(api, filter)}
            queryKey={['RejectedTraffic']}
            queryClient={queryClient}
            pageTitle="BackOfficeRejectedTraffic"
            storagePath="rejectedTraffic/columns"
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey="BackOfficeId" />,
                    width: 70,
                    minWidth: 70,
                },
                {
                    key: 'LogoVirtualPath',
                    name: <Trans i18nKey="BackOfficeLogo" />,
                    resizable: false,
                    cellClass: 'logo-cell',
                    formatter: logoVirtualPathFormatter,
                },
                {
                    key: 'leadgenId',
                    name: <Trans i18nKey="BackOfficeLeadgen" />,
                },
                {
                    key: 'isActive',
                    name: <Trans i18nKey="BackOfficeIsActive" />,
                    cellClass: 'cell-centered',
                    headerCellClass: 'cell-centered',
                    resizable: false,
                    formatter: isActiveFormatter,
                },
                {
                    key: 'referralLink',
                    name: <Trans i18nKey="BackOfficeReferralLink" />,
                },
                {
                    key: 'rule',
                    name: <Trans i18nKey="BackOfficeRejectedTrafficRule" />,
                },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'cell-centered no-change-modal',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    minWidth: 70,
                    draggable: false,
                    resizable: false,
                    clickDisable: true,
                    formatter: deleteActionBtnFormatter(hasRole('rejectedTrafficDelete')),
                },
            ]}
        />
    );
};

const useRejectedTrafficWorkplaceState = () =>
    useWorkplaceState<
        RejectedTraffic,
        RejectedTrafficInfo,
        RejectedTrafficChange,
        RejectedTrafficAdd
    >();

export { useRejectedTrafficWorkplaceState };
export default RejectedTrafficWP;
